
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import RapportTypography from './RapportTypography';
import { IRapportTextControl } from './RapportMallTypes';
import {
  getPositionStyle,
  ALIGN_OPTIONS,
  VALIGN_OPTIONS,
  TEXTALIGN_OPTIONS,
  SELECTED_SHADOW,
  AVENY_REPORT_DEFAULT_BAND_HEIGHT,
} from './RapportMallUtils';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Autocomplete from '@mui/material/Autocomplete';
import { RapportKontrollEditPosition } from './RapportKontrollEditPosition';
import EDTypography from '../Typography/EDTypography';
import { RapportKontrollHeader } from './RapportKontrollHeader';
import { RapporteditorTextField } from '../Rapporteditor/RapporteditorTextField';

export interface IProps {
  ctrl: IRapportTextControl;
  sx?: any;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onSelect?: (path: number[]) => void;
  onUpdate?: (updatedControl: IRapportTextControl) => void;
  controlPath: number[];
  selected?: boolean;
  maxWidth: number;
}

export const RapportText: React.FC<IProps> = props => {
  const { ctrl, sx, onMouseEnter, onMouseLeave, selected, maxWidth } = props;
  const handleSelect = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (props.onSelect) {
      props.onSelect(props.controlPath);
    }
  };
  return (
    <Box
      sx={{
        boxShadow: selected ? SELECTED_SHADOW : undefined,
        boxSizing: 'border-box',
        ...getPositionStyle(
          ctrl,
          props.controlPath.length > 1,
          maxWidth,
          AVENY_REPORT_DEFAULT_BAND_HEIGHT
        ),
        ...sx,
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={handleSelect}
      data-control-path={props.controlPath.join('-')}
    >
      <RapportTypography
        Style={ctrl.Style}
        lineHeight={ctrl.Height ?? undefined}
        Size={ctrl.Size}
      >
        {ctrl.Text}
      </RapportTypography>
    </Box>
  );
};

interface IRapportTextEditProps {
  value: IRapportTextControl;
  onChange: (ctrl: IRapportTextControl) => void;
}

export const RapportTextEdit: React.FC<IRapportTextEditProps> = props => (
  <Box sx={{ mt: 2 }}>
    {/* Värde */}
    <Box sx={{ mb: 3 }}>
      <RapportKontrollHeader value={props.value} />
      <RapporteditorTextField
        label="Text"
        field="Text"
        value={props.value}
        onChange={props.onChange}
        allowModelSelect
      />
    </Box>
    {/* Format */}
    <Box sx={{ mb: 3 }}>
      <EDTypography variant="subtitle2" sx={{ mb: 1 }}>
        Format
      </EDTypography>
      <FormControlLabel
        control={
          <Checkbox
            checked={!!props.value.Multiline}
            onChange={e =>
              props.onChange({ ...props.value, Multiline: e.target.checked })
            }
          />
        }
        label="Multiline"
        sx={{ mb: 2 }}
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={!!props.value.Formatted}
            onChange={e =>
              props.onChange({ ...props.value, Formatted: e.target.checked })
            }
          />
        }
        label="Formatted"
        sx={{ mb: 2 }}
      />
      <TextField
        label="Fontstorlek"
        value={props.value.Size ?? ''}
        inputProps={{ maxLength: 4, pattern: '\\d*' }}
        onChange={e =>
          props.onChange({
            ...props.value,
            Size: e.target.value === '' ? undefined : Number(e.target.value),
          })
        }
        fullWidth
        size="small"
        sx={{ mb: 2 }}
      />
      <TextField
        label="Stil"
        value={props.value.Style ?? ''}
        onChange={e =>
          props.onChange({ ...props.value, Style: e.target.value })
        }
        fullWidth
        size="small"
        sx={{ mb: 2 }}
      />
      <Autocomplete
        options={ALIGN_OPTIONS}
        getOptionLabel={option => option.label}
        value={
          ALIGN_OPTIONS.find(opt => opt.value === props.value.Align) || null
        }
        onChange={(_, newValue) =>
          props.onChange({
            ...props.value,
            Align: newValue?.value ?? undefined,
          })
        }
        renderInput={params => (
          <TextField
            {...params}
            label="Horisontell justering"
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />
        )}
        isOptionEqualToValue={(option, val) => option.value === val.value}
      />
      <Autocomplete
        options={VALIGN_OPTIONS}
        getOptionLabel={option => option.label}
        value={
          VALIGN_OPTIONS.find(opt => opt.value === props.value.VAlign) || null
        }
        onChange={(_, newValue) =>
          props.onChange({
            ...props.value,
            VAlign: newValue?.value ?? undefined,
          })
        }
        renderInput={params => (
          <TextField
            {...params}
            label="Vertikal justering"
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />
        )}
        isOptionEqualToValue={(option, val) => option.value === val.value}
      />
      <Autocomplete
        options={TEXTALIGN_OPTIONS}
        getOptionLabel={option => option.label}
        value={
          TEXTALIGN_OPTIONS.find(opt => opt.value === props.value.TextAlign) ||
          null
        }
        onChange={(_, newValue) =>
          props.onChange({
            ...props.value,
            TextAlign: newValue?.value ?? undefined,
          })
        }
        renderInput={params => (
          <TextField
            {...params}
            label="Textjustering"
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />
        )}
        isOptionEqualToValue={(option, val) => option.value === val.value}
      />
    </Box>
    <RapportKontrollEditPosition
      value={props.value}
      onChange={props.onChange}
    />
  </Box>
);
